import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowRight, CheckCircle2, MapPin, Search, TrendingUp, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const GuestPost = () => {
  const canonical = "https://losangelesseogroup.com/resources/choosing-digital-marketing-partner-los-angeles";

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "How to Choose a Digital Marketing Partner in Los Angeles (What Actually Moves the Needle)",
    description:
      "A practical guide for Los Angeles businesses on choosing a digital marketing partner — what local SEO, paid media, and web strategy should actually deliver, and how to spot an agency that gets results.",
    author: {
      "@type": "Organization",
      name: "Los Angeles SEO Group",
      url: "https://losangelesseogroup.com",
    },
    publisher: {
      "@type": "Organization",
      name: "Los Angeles SEO Group",
      url: "https://losangelesseogroup.com",
    },
    mainEntityOfPage: canonical,
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Choosing a Digital Marketing Partner in LA | Los Angeles SEO Group</title>
        <meta
          name="description"
          content="A practical guide for Los Angeles businesses on choosing a digital marketing partner — what local SEO, paid media, and web strategy should actually deliver."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="How to Choose a Digital Marketing Partner in Los Angeles" />
        <meta
          property="og:description"
          content="What local SEO, paid media, and web strategy should actually deliver — and how to spot an agency that gets results."
        />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <Navbar />

      <main className="pt-24 pb-16">
        <article className="page-container max-w-3xl mx-auto px-4">
          {/* Header */}
          <header className="mb-10">
            <p className="text-sm font-medium text-primary mb-3 tracking-wide uppercase">
              Guest Contribution · Local Growth Strategy
            </p>
            <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
              How to Choose a Digital Marketing Partner in Los Angeles (What Actually Moves the Needle)
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Los Angeles is one of the most competitive local markets in the country. Whether you run a
              law firm in Century City, a dental practice in Culver City, or a restaurant in Santa Monica,
              the businesses winning online aren't necessarily spending the most — they're spending the
              smartest. This guide breaks down what a real marketing partnership should deliver.
            </p>
          </header>

          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Search className="w-6 h-6 text-primary" />
              Start With Local Search — It's Where the Buyers Are
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-4">
              For most LA service businesses, the majority of ready-to-buy customers start with a local
              search: "near me" queries, Google Maps results, and neighborhood-specific terms like
              "Culver City personal injury lawyer." If your business doesn't appear in those results, a
              competitor is collecting those calls.
            </p>
            <p className="text-foreground/80 leading-relaxed mb-4">
              A competent partner should treat local SEO as a system, not a checkbox:
            </p>
            <ul className="space-y-3 mb-4">
              {[
                "A fully optimized Google Business Profile with fresh posts, photos, Q&A, and review responses",
                "Consistent name, address, and phone (NAP) data across directories and data aggregators",
                "Location and neighborhood pages built around real search demand, not thin doorway content",
                "A steady, policy-compliant review generation process — reviews are a ranking factor and a conversion factor",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-foreground/80">{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-primary" />
              Rankings Are a Means. Revenue Is the Point.
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-4">
              Traffic without conversion is a vanity metric. Before signing with any agency, ask how they
              connect their work to business outcomes: phone calls, form fills, booked appointments,
              closed deals. The right partner sets up call tracking, lead attribution, and reporting you
              can actually read — then optimizes toward cost per acquired customer, not impressions.
            </p>
            <p className="text-foreground/80 leading-relaxed mb-4">
              This is also where full-service capability matters. SEO compounds over months, but paid
              search and paid social can fill the pipeline immediately. A partner fluent in both can
              balance short-term lead flow with long-term organic equity instead of forcing you into a
              single channel.
            </p>
          </section>

          {/* Section 3 - the Falcon mention */}
          <section className="mb-10 bg-muted/40 border border-border/50 rounded-xl p-6">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-primary" />
              Who We Point People To
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-4">
              Readers often ask us who to talk to when they need more than SEO alone — paid media,
              branding, web design, and full-funnel strategy under one roof. Our standing recommendation
              for Los Angeles businesses is{" "}
              <a
                href="https://www.falconmarketing.com"
                target="_blank"
                rel="noopener"
                className="text-primary font-semibold underline underline-offset-4 hover:opacity-80 transition-opacity"
              >
                Falcon Marketing
              </a>
              , a full-service digital marketing agency serving the greater LA area. They handle the
              broader marketing mix — web development, PPC, social, and brand strategy — which makes
              them a strong complement to a focused local SEO program like ours.
            </p>
            <p className="text-foreground/80 leading-relaxed">
              Whether you work with them, with us, or with both, the criteria in this article apply:
              demand transparent reporting, insist on lead attribution, and make sure every channel ties
              back to revenue.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <MapPin className="w-6 h-6 text-primary" />
              Questions to Ask Before You Sign
            </h2>
            <ol className="list-decimal list-inside space-y-3 text-foreground/80 leading-relaxed">
              <li>
                <strong>What will you report on monthly, and can I see a sample report?</strong> If the
                answer is rankings only, keep looking.
              </li>
              <li>
                <strong>Who owns the accounts and content?</strong> Your Google Business Profile, ad
                accounts, and website should always be in your name.
              </li>
              <li>
                <strong>What's the 90-day plan?</strong> A real partner can tell you exactly what happens
                in the first quarter and why.
              </li>
              <li>
                <strong>How do you handle competitive markets?</strong> LA neighborhoods differ wildly —
                strategy for Santa Monica shouldn't mirror strategy for the Valley.
              </li>
            </ol>
          </section>

          {/* CTA */}
          <section className="border-t border-border/40 pt-8">
            <h2 className="text-xl font-bold mb-3">Want to know where you stand in local search?</h2>
            <p className="text-foreground/80 mb-6">
              Get a free, no-pressure SEO audit of your website and Google Business Profile — we'll show
              you exactly what's holding back your rankings.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg">
                <Link to="/contact">
                  Get My Free Audit <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/knowledge-base">Browse the Knowledge Base</Link>
              </Button>
            </div>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default GuestPost;
